// src/pages/ProjectTeam.jsx

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { db } from '../firebase/firebase';
import collaborationService from '../services/collaborationService';
import { ArrowLeft, UserPlus, Users, Mail, Trash2, Check, X as XIcon, Search } from 'lucide-react';

const ptAvatarUrl = (u) => {
  if (u?.profilePictureUrl && u.profilePictureUrl !== '/crewchainDefault.png') return u.profilePictureUrl;
  if (u?.photoURL && u.photoURL !== '/crewchainDefault.png') return u.photoURL;
  return null;
};
const ptInitials = (name) => {
  const parts = (name || '').trim().split(/\s+/);
  return parts.length >= 2 ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase() : (name?.[0] ?? '?').toUpperCase();
};

const ProjectTeam = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const auth = getAuth();
  const user = auth.currentUser;

  const [lightbox, setLightbox] = useState(null);
  const [project, setProject] = useState(null);
  const [projectOwner, setProjectOwner] = useState(null);
  const [teamMembers, setTeamMembers] = useState([]);
  const [pendingRequests, setPendingRequests] = useState([]); // Self-requested access, awaiting owner approval
  const [isApproving, setIsApproving] = useState(null);
  const [isDenying, setIsDenying] = useState(null);
  const [allCompanyPms, setAllCompanyPms] = useState([]);
  const [pmSearch, setPmSearch] = useState('');
  const [inviteRoles, setInviteRoles] = useState({});
  const [isInviting, setIsInviting] = useState(null);
  const [isRemoving, setIsRemoving] = useState(null);
  const [isReinviting, setIsReinviting] = useState(null);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');
  const [permissions, setPermissions] = useState({ canEdit: false, isOwner: false, isCollaborator: false });
  const [hoveredMemberId, setHoveredMemberId] = useState(null);
  const [hoveredButtonId, setHoveredButtonId] = useState(null);

  useEffect(() => {
    if (projectId) {
      loadProjectData();
      checkPermissions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, user]);

  const loadProjectData = async () => {
    try {
      // Load project info
      const projectRef = doc(db, 'projects', projectId);
      const projectSnap = await getDoc(projectRef);
      
      if (projectSnap.exists()) {
        const projectData = { id: projectSnap.id, ...projectSnap.data() };
        setProject(projectData);

        // Load project owner info
        const ownerRef = doc(db, 'users', projectData.createdBy);
        const ownerSnap = await getDoc(ownerRef);
        let ownerData = null;
        if (ownerSnap.exists()) {
          ownerData = { id: ownerSnap.id, ...ownerSnap.data() };
          setProjectOwner(ownerData);
        }

        // Load all PMs in the same company for the lookup panel
        if (ownerData?.companyId) {
          const pmSnap = await getDocs(query(
            collection(db, 'users'),
            where('companyId', '==', ownerData.companyId),
            where('role', '==', 'pm')
          ));
          setAllCompanyPms(pmSnap.docs.map(d => ({ id: d.id, ...d.data() })));
        }

        // Load team members (collaborators) and enrich with profile photos
        const collaborators = await collaborationService.getProjectCollaborators(projectId);
        const enriched = await Promise.all(
          collaborators.map(async (c) => {
            if (!c.userId) return c;
            try {
              const uSnap = await getDoc(doc(db, 'users', c.userId));
              if (uSnap.exists()) {
                const ud = uSnap.data();
                const fullName = [ud.firstName, ud.lastName].filter(Boolean).join(' ').trim();
                return {
                  ...c,
                  profilePictureUrl: ud.profilePictureUrl,
                  photoURL: ud.photoURL,
                  ...(fullName ? { userName: fullName } : {}),
                };
              }
            } catch { /* non-fatal */ }
            return c;
          })
        );

        // Self-requested access (invitedBy === userId) awaiting the owner's approval is
        // shown separately, not mixed into the regular team members list.
        const requests = enriched.filter(c => c.status === 'pending' && c.invitedBy === c.userId);
        const members = enriched.filter(c => !(c.status === 'pending' && c.invitedBy === c.userId));
        setPendingRequests(requests);
        setTeamMembers(members);
      }
    } catch (error) {
      console.error('Error loading project data:', error);
      showMessage('Failed to load project team data', 'error');
    }
  };

  const checkPermissions = async () => {
    try {
      const perms = await collaborationService.checkProjectPermissions(projectId, user?.uid);
      setPermissions(perms);
    } catch (error) {
      console.error('Error checking permissions:', error);
    }
  };

  const showMessage = (text, type) => {
    setMessage(text);
    setMessageType(type);
    setTimeout(() => {
      setMessage('');
      setMessageType('');
    }, 3000);
  };

  const handleAddPm = async (pm) => {
    const role = inviteRoles[pm.id] || 'pm_collaborator';
    const wasRequesting = pendingRequests.some(r => r.userId === pm.id);
    setIsInviting(pm.id);
    try {
      const result = await collaborationService.invitePMCollaboratorByEmail(
        projectId,
        pm.email,
        role
      );
      if (result.success) {
        showMessage(
          wasRequesting
            ? `${pm.firstName || pm.email}'s request approved.`
            : `${pm.firstName || pm.email} added to project.`,
          'success'
        );
        loadProjectData();
      } else {
        showMessage(result.message, 'error');
      }
    } catch (error) {
      console.error('Error inviting team member:', error);
      showMessage('Failed to send invitation', 'error');
    } finally {
      setIsInviting(null);
    }
  };

  const handleReinvite = async (member) => {
    setIsReinviting(member.id);
    try {
      const result = await collaborationService.reinviteCollaborator(projectId, member);
      if (result.success) {
        showMessage(result.message, 'success');
        loadProjectData();
      } else {
        showMessage(result.message, 'error');
      }
    } catch (error) {
      console.error('Error re-inviting:', error);
      showMessage('Failed to re-invite', 'error');
    } finally {
      setIsReinviting(null);
    }
  };

  const handleRemoveMember = async (memberId, memberName) => {
    if (!window.confirm(`Remove ${memberName} from this project team?`)) {
      return;
    }

    setIsRemoving(memberId);
    try {
      const result = await collaborationService.removeCollaborator(projectId, memberId);
      
      if (result.success) {
        showMessage(result.message, 'success');
        loadProjectData(); // Refresh all data
      } else {
        showMessage(result.message, 'error');
      }
    } catch (error) {
      console.error('Error removing team member:', error);
      showMessage('Failed to remove team member', 'error');
    } finally {
      setIsRemoving(null);
    }
  };

  const handleApproveRequest = async (request) => {
    setIsApproving(request.id);
    try {
      const result = await collaborationService.approveCollaboratorRequest(projectId, request.id);
      if (result.success) {
        showMessage(`${request.userName || request.userEmail} now has collaborator access.`, 'success');
        loadProjectData();
      } else {
        showMessage(result.message, 'error');
      }
    } catch (error) {
      console.error('Error approving access request:', error);
      showMessage('Failed to approve request', 'error');
    } finally {
      setIsApproving(null);
    }
  };

  const handleDenyRequest = async (request) => {
    setIsDenying(request.id);
    try {
      const result = await collaborationService.removeCollaborator(projectId, request.id);
      if (result.success) {
        showMessage('Access request denied.', 'success');
        loadProjectData();
      } else {
        showMessage(result.message, 'error');
      }
    } catch (error) {
      console.error('Error denying access request:', error);
      showMessage('Failed to deny request', 'error');
    } finally {
      setIsDenying(null);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'accepted': return '#10b981';
      case 'pending': return '#f59e0b';
      case 'declined': return '#ef4444';
      default: return '#6b7280';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'accepted': return <Check size={16} />;
      case 'pending': return <Mail size={16} />;
      case 'declined': return <XIcon size={16} />;
      default: return null;
    }
  };

  const getRoleDisplayName = (role) => {
    switch (role) {
      case 'pm_collaborator': return 'Collaborator';
      case 'pm_viewer': return 'Viewer';
      default: return role;
    }
  };

  if (!project) {
    return (
      <div style={{ padding: 40, textAlign: 'center' }}>
        <p>Loading project team...</p>
      </div>
    );
  }

  return (
    <>
    <div style={{ padding: 40, backgroundColor: 'var(--branding-app-surface)', minHeight: '100vh' }}>
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
      {/* Header */}
      <div style={{ marginBottom: 32 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 16 }}>
          <button
            onClick={() => navigate(`/project/${projectId}`)}
            style={{
              background: 'transparent',
              border: 'none',
              color: 'var(--cc-text-muted)',
              cursor: 'pointer',
              padding: '8px',
              borderRadius: '6px',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
          >
            <ArrowLeft size={20} />
            Back to project
          </button>
        </div>
        
        <h1 style={{
          fontSize: 28,
          fontWeight: 700,
          color: 'var(--cc-text-primary)',
          margin: '0 0 8px 0'
        }}>
          Project team
        </h1>
        <p style={{
          fontSize: 16,
          color: 'var(--cc-text-muted)',
          margin: 0
        }}>
          {project.name} • {project.projectCode}
        </p>
      </div>

      {/* Message */}
      {message && (
        <div style={{
          padding: '12px 16px',
          borderRadius: '8px',
          marginBottom: '24px',
          background: messageType === 'success' ? '#f0fdf4' : '#fef2f2',
          color: messageType === 'success' ? '#166534' : '#dc2626',
          fontSize: '14px',
          fontWeight: '500'
        }}>
          {message}
        </div>
      )}

      {/* Requests awaiting approval - self-requested access from company account owners */}
      {permissions.isOwner && pendingRequests.length > 0 && (
        <div style={{ marginBottom: 32 }}>
          <h2 style={{
            fontSize: 20,
            fontWeight: 600,
            color: 'var(--cc-text-primary)',
            margin: '0 0 16px 0',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <Mail size={20} />
            Requests awaiting your approval ({pendingRequests.length})
          </h2>
          <div style={{
            background: 'var(--cc-card-bg)',
            borderRadius: '12px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            overflow: 'hidden'
          }}>
            {pendingRequests.map((request, idx) => {
              const avatar = ptAvatarUrl(request);
              return (
                <div
                  key={request.id}
                  style={{
                    padding: '14px 20px',
                    borderBottom: idx < pendingRequests.length - 1 ? '1px solid var(--branding-app-surface)' : 'none',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '14px',
                  }}
                >
                  <div style={{ width: 44, height: 44, borderRadius: '50%', overflow: 'hidden', flexShrink: 0, background: avatar ? 'transparent' : 'var(--branding-app-surface-panel-border)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 700, color: 'var(--branding-app-surface-panel-fg)' }}>
                    {avatar ? <img src={avatar} alt={request.userName} style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : ptInitials(request.userName)}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--cc-text-primary)' }}>{request.userName || request.userEmail}</div>
                    <div style={{ fontSize: 12, color: 'var(--cc-text-muted)' }}>Requesting {getRoleDisplayName(request.role)} access</div>
                  </div>
                  <button
                    onClick={() => handleApproveRequest(request)}
                    disabled={isApproving === request.id || isDenying === request.id}
                    style={{ display: 'flex', alignItems: 'center', gap: 4, background: '#f0fdf4', border: '1px solid #86efac', color: '#166534', cursor: 'pointer', padding: '6px 12px', borderRadius: 6, fontSize: 12, fontWeight: 600, opacity: isApproving === request.id ? 0.6 : 1 }}
                  >
                    <Check size={14} />
                    {isApproving === request.id ? 'Approving…' : 'Approve'}
                  </button>
                  <button
                    onClick={() => handleDenyRequest(request)}
                    disabled={isApproving === request.id || isDenying === request.id}
                    style={{ display: 'flex', alignItems: 'center', gap: 4, background: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626', cursor: 'pointer', padding: '6px 12px', borderRadius: 6, fontSize: 12, fontWeight: 600, opacity: isDenying === request.id ? 0.6 : 1 }}
                  >
                    <XIcon size={14} />
                    {isDenying === request.id ? 'Denying…' : 'Deny'}
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 400px', gap: 32 }}>
        {/* Team Members List */}
        <div>
          <h2 style={{
            fontSize: 20,
            fontWeight: 600,
            color: 'var(--cc-text-primary)',
            margin: '0 0 20px 0',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <Users size={20} />
            Team members ({teamMembers.length + 1})
          </h2>

          <div style={{
            background: 'var(--cc-card-bg)',
            borderRadius: '12px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            overflow: 'hidden'
          }}>
            {/* Project Owner */}
            {projectOwner && (() => {
              const ownerName = [projectOwner.firstName, projectOwner.lastName].filter(Boolean).join(' ') || projectOwner.displayName || projectOwner.email;
              const ownerAvatar = ptAvatarUrl(projectOwner);
              const isOwnerCurrentUser = projectOwner.id === user?.uid;
              return (
                <div style={{
                  padding: '16px 20px',
                  borderBottom: '1px solid var(--branding-app-surface)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '14px',
                  background: 'transparent',
                }}>
                  <div
                    onClick={() => ownerAvatar && setLightbox({ url: ownerAvatar, name: ownerName })}
                    style={{ width: 56, height: 56, borderRadius: '50%', overflow: 'hidden', flexShrink: 0, background: ownerAvatar ? 'transparent' : 'var(--branding-app-surface-panel-border)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, fontWeight: 700, color: 'var(--branding-app-surface-panel-fg)', cursor: ownerAvatar ? 'pointer' : 'default' }}
                  >
                    {ownerAvatar
                      ? <img src={ownerAvatar} alt={ownerName} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      : ptInitials(ownerName)
                    }
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--cc-text-primary)', marginBottom: 2, display: 'flex', alignItems: 'center', gap: 8 }}>
                      {ownerName}
                      {isOwnerCurrentUser && <span style={{ fontSize: 11, color: 'var(--cc-text-muted)', fontStyle: 'italic' }}>you</span>}
                      <span style={{ fontSize: 11, color: 'var(--branding-app-surface-panel-muted)', background: 'var(--branding-app-surface)', padding: '2px 8px', borderRadius: 12, fontWeight: 600 }}>Owner</span>
                    </div>
                    <div style={{ fontSize: 13, color: 'var(--cc-text-muted)' }}>{projectOwner.email}</div>
                  </div>
                </div>
              );
            })()}

            {/* Team Members */}
            {teamMembers.length === 0 ? (
              <div style={{
                padding: '40px 20px',
                textAlign: 'center',
                color: 'var(--cc-text-muted)'
              }}>
                <Users size={48} style={{ marginBottom: '12px', opacity: 0.5 }} />
                <p style={{ margin: 0, fontSize: '14px' }}>
                  No team members yet
                </p>
              </div>
            ) : (
              teamMembers.map((member) => {
                const isCurrentUser = member.userId === user?.uid;
                const memberAvatar = ptAvatarUrl(member);
                return (
                <div
                  key={member.id}
                  style={{
                    padding: '14px 20px',
                    borderBottom: '1px solid var(--branding-app-surface)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '14px',
                    transition: 'background-color 0.15s',
                    background: hoveredMemberId === member.id ? 'var(--branding-app-surface-panel)' : 'transparent',
                  }}
                  onMouseEnter={() => setHoveredMemberId(member.id)}
                  onMouseLeave={() => setHoveredMemberId(null)}
                >
                  <div
                    onClick={() => memberAvatar && setLightbox({ url: memberAvatar, name: member.userName })}
                    style={{ width: 56, height: 56, borderRadius: '50%', overflow: 'hidden', flexShrink: 0, background: memberAvatar ? 'transparent' : 'var(--branding-app-surface-panel-border)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, fontWeight: 700, color: 'var(--branding-app-surface-panel-fg)', cursor: memberAvatar ? 'pointer' : 'default' }}
                  >
                    {memberAvatar
                      ? <img src={memberAvatar} alt={member.userName} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      : ptInitials(member.userName)
                    }
                  </div>

                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 15, fontWeight: 500, color: 'var(--cc-text-primary)', marginBottom: 2, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      {member.userName}
                      {isCurrentUser && <span style={{ fontSize: 11, color: 'var(--cc-text-muted)', fontStyle: 'italic' }}>you</span>}
                      <span style={{ fontSize: 11, color: 'var(--cc-text-muted)', background: 'var(--branding-app-surface)', padding: '2px 8px', borderRadius: 12, fontWeight: 500 }}>
                        {getRoleDisplayName(member.role)}
                      </span>
                    </div>
                    <div style={{ fontSize: 13, color: 'var(--cc-text-muted)', marginBottom: 3 }}>{member.userEmail}</div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: getStatusColor(member.status), fontSize: 12, fontWeight: 500 }}>
                      {getStatusIcon(member.status)}
                      {member.status}
                      {permissions.isOwner && member.status === 'declined' && (
                        <button
                          onClick={() => handleReinvite(member)}
                          disabled={isReinviting === member.id}
                          style={{ marginLeft: 4, background: '#f0fdf4', border: '1px solid #86efac', color: '#166534', cursor: isReinviting === member.id ? 'not-allowed' : 'pointer', padding: '4px 8px', borderRadius: 6, fontSize: 12, fontWeight: 500, opacity: isReinviting === member.id ? 0.6 : 1 }}
                          title="Re-invite (reuses last role)"
                        >
                          {isReinviting === member.id ? 'Sending…' : 'Re-invite'}
                        </button>
                      )}
                    </div>
                  </div>
                  
                  {permissions.isOwner && member.status === 'accepted' && (
                    <button
                      onClick={() => handleRemoveMember(member.id, member.userName)}
                      disabled={isRemoving === member.id}
                      style={{
                        background: hoveredButtonId === member.id && isRemoving !== member.id ? '#fef2f2' : 'transparent',
                        border: 'none',
                        color: '#ef4444',
                        cursor: isRemoving === member.id ? 'not-allowed' : 'pointer',
                        padding: '8px',
                        borderRadius: '6px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        opacity: isRemoving === member.id ? 0.5 : 1,
                        transition: 'background-color 0.2s'
                      }}
                      onMouseEnter={() => setHoveredButtonId(member.id)}
                      onMouseLeave={() => setHoveredButtonId(null)}
                      title="Remove team member"
                    >
                      <Trash2 size={18} />
                    </button>
                  )}
                </div>
              );})
            )}
          </div>
        </div>

        {/* Add Team Members */}
        {permissions.isOwner && (
          <div>
              <h2 style={{
                fontSize: 20,
                fontWeight: 600,
                color: 'var(--cc-text-primary)',
                margin: '0 0 20px 0',
                display: 'flex',
                alignItems: 'center',
                    gap: '8px'
              }}>
                <UserPlus size={20} />
                Add team member
              </h2>

            <div style={{ background: 'var(--cc-card-bg)', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
              {/* Search */}
              <div style={{ padding: '16px 16px 12px', borderBottom: '1px solid var(--branding-app-surface)' }}>
                <div style={{ position: 'relative' }}>
                  <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--cc-text-muted)', pointerEvents: 'none' }} />
                  <input
                    type="text"
                    value={pmSearch}
                    onChange={(e) => setPmSearch(e.target.value)}
                    placeholder="Search by name or email…"
                    style={{ width: '100%', padding: '8px 10px 8px 30px', border: '1px solid var(--branding-app-surface-panel-border)', borderRadius: 8, fontSize: 13, background: 'var(--branding-app-surface)', color: 'var(--cc-text-primary)', outline: 'none', boxSizing: 'border-box' }}
                  />
                </div>
              </div>

              {/* PM list */}
              {(() => {
                const onProjectIds = new Set([
                  projectOwner?.id,
                  ...teamMembers.map(m => m.userId).filter(Boolean),
                ]);
                const term = pmSearch.trim().toLowerCase();
                const available = allCompanyPms.filter(pm => {
                  if (onProjectIds.has(pm.id)) return false;
                  if (!term) return true;
                  const name = [pm.firstName, pm.lastName].filter(Boolean).join(' ').toLowerCase();
                  return name.includes(term) || (pm.email || '').toLowerCase().includes(term);
                });

                if (available.length === 0) {
                  return (
                    <div style={{ padding: '32px 20px', textAlign: 'center', color: 'var(--cc-text-muted)', fontSize: 13 }}>
                      {term ? 'No colleagues match that search.' : 'Everyone in your company is already on this project.'}
                    </div>
                  );
                }

                return (
                  <ul style={{ listStyle: 'none', padding: 0, margin: 0, maxHeight: 380, overflowY: 'auto' }}>
                    {available.map((pm, idx) => {
                      const name = [pm.firstName, pm.lastName].filter(Boolean).join(' ') || pm.email;
                      const avatar = ptAvatarUrl(pm);
                      const role = inviteRoles[pm.id] || 'pm_collaborator';
                      const busy = isInviting === pm.id;
                      // Already asked to join - clicking "Invite" here is the same action as
                      // approving their request (collaborationService detects this too), but
                      // label it accurately so it doesn't read as a fresh invite.
                      const hasPendingRequest = pendingRequests.some(r => r.userId === pm.id);
                      return (
                        <li key={pm.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', borderBottom: idx < available.length - 1 ? '1px solid var(--branding-app-surface)' : 'none' }}>
                          <div style={{ width: 44, height: 44, borderRadius: '50%', overflow: 'hidden', flexShrink: 0, background: avatar ? 'transparent' : 'var(--branding-app-surface-panel-border)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 700, color: 'var(--branding-app-surface-panel-fg)' }}>
                            {avatar ? <img src={avatar} alt={name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : ptInitials(name)}
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--cc-text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{name}</div>
                            <div style={{ fontSize: 11, color: 'var(--cc-text-muted)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{pm.email}</div>
                          </div>
                          <select
                            value={role}
                            onChange={(e) => setInviteRoles(prev => ({ ...prev, [pm.id]: e.target.value }))}
                            style={{ fontSize: 11, padding: '4px 6px', border: '1px solid var(--branding-app-surface-panel-border)', borderRadius: 6, background: 'var(--branding-app-surface)', color: 'var(--cc-text-primary)', cursor: 'pointer', flexShrink: 0 }}
                          >
                            <option value="pm_collaborator">Collaborator</option>
                            <option value="pm_viewer">Viewer</option>
                          </select>
                          <button
                            type="button"
                            onClick={() => handleAddPm(pm)}
                            disabled={busy}
                            title={hasPendingRequest ? `${name} has already requested to join - this will approve them` : undefined}
                            style={{ flexShrink: 0, fontSize: 11, fontWeight: 600, padding: '5px 12px', background: busy ? 'var(--branding-app-surface)' : 'var(--branding-app-primary)', color: busy ? 'var(--cc-text-muted)' : 'var(--branding-app-primary-fg)', border: 'none', borderRadius: 6, cursor: busy ? 'wait' : 'pointer' }}
                          >
                            {busy ? '…' : hasPendingRequest ? 'Approve request' : 'Add'}
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                );
              })()}
            </div>
          </div>
        )}
      </div>
    </div>

    {lightbox && (
      <div
        onClick={() => setLightbox(null)}
        style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', zIndex: 2000, cursor: 'zoom-out', padding: 24 }}
      >
        <img
          src={lightbox.url}
          alt={lightbox.name}
          onClick={(e) => e.stopPropagation()}
          style={{ maxWidth: '90vw', maxHeight: '80vh', borderRadius: 12, objectFit: 'contain', boxShadow: '0 25px 60px rgba(0,0,0,0.5)', cursor: 'default' }}
        />
        {lightbox.name && (
          <div style={{ marginTop: 16, fontSize: 15, fontWeight: 600, color: '#f1f5f9' }}>{lightbox.name}</div>
        )}
      </div>
    )}
    </>
  );
};

export default ProjectTeam;
